import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Amplify, Auth } from 'aws-amplify'
import { generalConfig } from "../lib/awsconfig";
import { CognitoIdentityProviderClient, InitiateAuthCommand, AuthFlowType} from "@aws-sdk/client-cognito-identity-provider";
import { CognitoUserSession, CognitoIdToken, CognitoAccessToken, CognitoRefreshToken } from "amazon-cognito-identity-js";
import { usePostHog } from 'posthog-js/react';
import { UserAccessAuth } from "../components/settings/DefaultUserAccess.ts";

interface UseAuth {
  isAuthenticated: boolean;
  isAdmin: boolean;
  username: string;
  mail: string;
  name: string;
  idToken: string;
  accessToken: string;
  refreshToken: string;
  customAttributes: { [key: string]: string };
  loading: boolean;
  salesPingLoading: boolean;
  ensureSalesWarmup: (force?: boolean) => Promise<void>;
  signIn: (email: string, password: string) => Promise<Result>;
  signUp: (email: string, password: string, name: string, whatsapp: string) => Promise<Result>;
  signOut: () => Promise<{success: boolean; message: string;}>;
  sendConfirmationEmail: (username: string) => Promise<Result>;
  confirmEmail: (username: string, code: string) => Promise<Result>;
  recoverPassword: (username: string) => Promise<Result>;
  sendRecoverPasswordCode: (username: string, code: string, newPassword: string) => Promise<Result>;

  creditsKey: number;
  setcreditsKey: React.Dispatch<React.SetStateAction<number>>;
}

interface Result {
  success: boolean;
  message: string;
}

type Props = {
  children?: React.ReactNode;
};

const SALES_WARMUP_KEY = 'salesWarmupPingedAt';
const SALES_WARMUP_TTL_MS = 15 * 60 * 1000;

const getSalesWarmupStamp = () => {
  try {
    const value = window.localStorage.getItem(SALES_WARMUP_KEY);
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const isSalesWarmupFresh = () => {
  const stamp = getSalesWarmupStamp();
  if (!stamp) return false;
  return Date.now() - stamp < SALES_WARMUP_TTL_MS;
};

const setSalesWarmupStamp = () => {
  try {
    window.localStorage.setItem(SALES_WARMUP_KEY, String(Date.now()));
  } catch {
    // no-op
  }
};

const clearSalesWarmupStamp = () => {
  try {
    window.localStorage.removeItem(SALES_WARMUP_KEY);
  } catch {
    // no-op
  }
};

let authState = {
  isAuthenticated: false,
  username: "",
  mail: "",
  name: "",
  isAdmin: false,
  idToken: "",
  accessToken: "",
  refreshToken: "",
  customAttributes: {}
};

export const setAuthState = (state: Partial<typeof authState>) => {
  authState = { ...authState, ...state };
};

export const getAuthState = () => authState;

const authContext = createContext({} as UseAuth);

export const ProvideAuth: React.FC<Props> = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
  return useContext(authContext);
};

const useProvideAuth = (): UseAuth => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [mail, setMail] = useState("");
  const [name, setName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [idToken, setIdToken] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [customAttributes, setCustomAttributes] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [salesPingLoading, setSalesPingLoading] = useState(false);
  const [creditsKey, setcreditsKey] = useState(0)
  const posthog = usePostHog();
  const salesWarmupInFlightRef = useRef(false);

  let intervalRef: NodeJS.Timeout | number | null = null;
  const iniciarCheckSession = (interval:number) => {
    if (!intervalRef) {
      intervalRef = setInterval(() => {checkSession()}, interval * 60 * 1000);
    }
  };

  const identifyUserInPostHog = (user: any, userAttributes: any, customAttrs: { [key: string]: string }) => {
    if (posthog && user) {
      const distinctId = user.attributes.sub;

      const properties = {
        email: user.attributes.email,
        name: user.attributes.name,
        ...customAttrs 
      };

      posthog.identify(distinctId, properties);
    }
  };

  const checkSession = async () => {
    Amplify.configure({
      Auth: {
        region: generalConfig.REGION,
        userPoolId: generalConfig.USER_POOL_ID,
        userPoolWebClientId: generalConfig.USER_POOL_APP_CLIENT_ID,
      },
    });
    console.log('checking session')
    try {
      let admin = false

      const client = new CognitoIdentityProviderClient({ region: "us-east-1" })
      const clientId = generalConfig.USER_POOL_APP_CLIENT_ID
      const params = {
        AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
        ClientId: clientId,
        AuthParameters: {
          REFRESH_TOKEN: authState.refreshToken,
        },
      }
      const command = new InitiateAuthCommand(params);
      const response = await client.send(command);

      if(response.AuthenticationResult == undefined) {
        const err = Error('No Tokens returned')
        err.name = 'TokenRefreshError'
        throw err
      }

      const newAccessToken = new CognitoAccessToken({AccessToken: response.AuthenticationResult.AccessToken!})
      const newIdToken = new CognitoIdToken({IdToken: response.AuthenticationResult.IdToken!})
      const newRefreshToken = new CognitoRefreshToken({RefreshToken: authState.refreshToken!})
      const newSession = new CognitoUserSession({
        IdToken: newIdToken,
        AccessToken: newAccessToken,
        RefreshToken: newRefreshToken
      })

      const result = await Auth.currentAuthenticatedUser()
      result.setSignInUserSession(newSession)
      const session = await Auth.currentSession();

      setUsername(result.username);
      setMail(result.attributes.email)
      setName(result.attributes.name)
      setIsAuthenticated(true);
      setIdToken(session.getIdToken().getJwtToken())
      setAccessToken(session.getAccessToken().getJwtToken())
      setRefreshToken(session.getRefreshToken().getToken())

      const attributes = await Auth.userAttributes(result);
      const customAttributes = attributes
            .filter(attr => attr.Name.startsWith('custom:'))
            .reduce((acc: { [key: string]: string }, attr) => {
                const attributeName = attr.Name.replace('custom:', '');
                acc[attributeName] = attr.Value;
                return acc;
            }, {});
      setCustomAttributes(customAttributes)

      if(session.getIdToken().decodePayload()['cognito:groups']) {
        setIsAdmin(session.getIdToken().decodePayload()['cognito:groups'].includes('admin'))
        if(session.getIdToken().decodePayload()['cognito:groups'].includes('admin')) admin = true
      }

      setAuthState({
        isAuthenticated: true,
        username: result.username,
        mail: result.attributes.email,
        name: result.attributes.name,
        isAdmin: admin,
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        customAttributes: customAttributes
      })
    } catch(error) {
      console.log(error)
      let kick = false
      if (authState.isAuthenticated) {
        kick = true
      }
      setUsername("");
      setMail("");
      setName("");
      setIsAuthenticated(false);
      setIsAdmin(false)
      setIdToken("")
      setAccessToken("")
      setRefreshToken("")

      setCustomAttributes({})

      setAuthState({
        isAuthenticated: false,
        username: '',
        mail: '',
        name: '',
        isAdmin: false,
        idToken: '',
        accessToken: '',
        refreshToken: '',
        customAttributes: {}
      })
      if (kick){
        await signOut()
        window.location.href = '/'
      }
    }
  }


  useEffect(() => {
    //if(!authState.isAuthenticated) return
    async function getInfo() {
      setLoading(true)
      Amplify.configure({
        Auth: {
          region: generalConfig.REGION,
          userPoolId: generalConfig.USER_POOL_ID,
          userPoolWebClientId: generalConfig.USER_POOL_APP_CLIENT_ID,
        },
      });
      console.log('checking session')
      /*
      if (!authState.isAuthenticated) {
        setLoading(false)
        return
      }
      */
      try {
        
        let admin = false
        const result = await Auth.currentAuthenticatedUser()
        setUsername(result.username);
        setMail(result.attributes.email)
        setName(result.attributes.name)
        setIsAuthenticated(true);

        const session = await Auth.currentSession();
        setIdToken(session.getIdToken().getJwtToken())
        setAccessToken(session.getAccessToken().getJwtToken())
        setRefreshToken(session.getRefreshToken().getToken())

        const attributes = await Auth.userAttributes(result);
        const customAttributes = attributes
              .filter(attr => attr.Name.startsWith('custom:'))
              .reduce((acc: { [key: string]: string }, attr) => {
                  const attributeName = attr.Name.replace('custom:', '');
                  acc[attributeName] = attr.Value;
                  return acc;
              }, {});
        setCustomAttributes(customAttributes)
        identifyUserInPostHog(result, result.attributes, customAttributes);
        if(session.getIdToken().decodePayload()['cognito:groups']) {
          setIsAdmin(session.getIdToken().decodePayload()['cognito:groups'].includes('admin'))
          if(session.getIdToken().decodePayload()['cognito:groups'].includes('admin')) admin = true
        }

        setAuthState({
          isAuthenticated: true,
          username: result.username,
          mail: result.attributes.email,
          name: result.attributes.name,
          isAdmin: admin,
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          customAttributes: customAttributes
        })
        
      } catch(error) {
          setUsername("");
          setMail("");
          setName("");
          setIsAuthenticated(false);
          setIsAdmin(false)
          setIdToken("")
          setAccessToken("")
          setRefreshToken("")

          setCustomAttributes({})

          setAuthState({
            isAuthenticated: false,
            username: '',
            mail: '',
            name: '',
            isAdmin: false,
            idToken: '',
            accessToken: '',
            refreshToken: '',
            customAttributes: {}
          })
      } finally {
        setLoading(false)
        iniciarCheckSession(0.5)
      }
    }
    getInfo()
  }, []);

  const ensureSalesWarmup = useCallback(async (force = false) => {
    const auth = getAuthState();
    if (!auth.isAuthenticated || Object.keys(auth.customAttributes).length === 0) {
      setSalesPingLoading(false);
      return;
    }
    if (salesWarmupInFlightRef.current) return;
    if (!force && isSalesWarmupFresh()) {
      setSalesPingLoading(false);
      return;
    }

    salesWarmupInFlightRef.current = true;
    try {
      const [{ checkUserPermissions }, { pingSales }] = await Promise.all([
        import("../lib/userPermissions"),
        import("../lib/pingSales")
      ]);
      const permissions = await checkUserPermissions();
      if (!permissions.analytics_venda) {
        setSalesPingLoading(false);
        return;
      }
      setSalesWarmupStamp();
      setSalesPingLoading(true);
      await pingSales();
    } catch (error) {
      console.warn('Error warming sales analytics:', error);
      clearSalesWarmupStamp();
    } finally {
      salesWarmupInFlightRef.current = false;
      setSalesPingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || loading || Object.keys(customAttributes).length === 0) {
      setSalesPingLoading(false);
      return;
    }
    void ensureSalesWarmup();
  }, [ensureSalesWarmup, isAuthenticated, loading, customAttributes]);

  const signIn = async (email: string, password: string) => {
    Amplify.configure({
      Auth: {
        region: generalConfig.REGION,
        userPoolId: generalConfig.USER_POOL_ID,
        userPoolWebClientId: generalConfig.USER_POOL_APP_CLIENT_ID,
      },
    });

    try {
      let result = await Auth.signIn(email, password);

      if (result.challengeName === "NEW_PASSWORD_REQUIRED") {
        console.log("Desafio de nova senha detectado.");
        result = await Auth.completeNewPassword(result, password);
        console.log("Nova senha configurada com sucesso.", result);
      }

      setUsername(result.username);
      setMail(result.attributes.email);
      setName(result.attributes.name);
      setIsAuthenticated(true);
      setIsAdmin(false)
      
      const session = await Auth.currentSession();

      setIdToken(session.getIdToken().getJwtToken())
      setAccessToken(session.getAccessToken().getJwtToken())
      setRefreshToken(session.getRefreshToken().getToken())

      const attributes = await Auth.userAttributes(result);
      const customAttributes = attributes
            .filter(attr => attr.Name.startsWith('custom:'))
            .reduce((acc: { [key: string]: string }, attr) => {
                const attributeName = attr.Name.replace('custom:', ''); // Remove o prefixo "custom:"
                acc[attributeName] = attr.Value;
                return acc;
            }, {});
      setCustomAttributes(customAttributes)

      if(session.getIdToken().decodePayload()['cognito:groups']) {
        setIsAdmin(session.getIdToken().decodePayload()['cognito:groups'].includes('admin'))
      }

      setAuthState({
        isAuthenticated: true,
        username: result.username,
        mail: result.attributes.email,
        name: result.attributes.name,
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        customAttributes: customAttributes
      })
      identifyUserInPostHog(result, result.attributes, customAttributes);
      void ensureSalesWarmup(true);
      return { success: true, message: "" };
    } catch (error) {
      console.log(error)
      if (error instanceof Error && error.name === "UserNotConfirmedException") {
        return { 
            success: false, 
            message: "Sua conta não foi confirmada. Verifique seu e-mail para ativar a conta.",
            confirmation: true
        };
    } 
    if (error instanceof Error && (error.name === "NotAuthorizedException" || error.name === "UserNotFoundException")) {
      return { 
          success: false, 
          message: "Nome de usuário ou senha incorretos.",
          confirmation: true
      };
    }
    
    return {
      success: false,
      message: "Erro ao fazer login, contate o Administrador.",
    };
    
      
    }
  };

  const signUp = async (email: string, password: string, name: string, whatsapp:string) => {
    Amplify.configure({
      Auth: {
        region: generalConfig.REGION,
        userPoolId: generalConfig.USER_POOL_ID,
        userPoolWebClientId: generalConfig.USER_POOL_APP_CLIENT_ID,
      },
    });

    try {
      const result = await Auth.signUp({
        "username": email,
        "password": password,
        attributes: {
          "email": email,
          "phone_number":whatsapp,
          "name": name,
          "custom:adm": '0',
          "custom:analytics": '0',
          "custom:integracoes": '0',
          "custom:ops-busca": '0',
          "custom:ops-historico": '1',
          "custom:ops-leads": '1',
          "custom:ops-gpt":'0',
          "custom:strategy": '0',
          "custom:AIV": "0",
          "custom:Access": UserAccessAuth()
          
        },
        autoSignIn: {
          enabled: false
        }
        
      })
      console.log(result)
      return { success: true, message: "" };
    } catch(e) {
      console.log(e)
      
      if (e instanceof Error && e.name == "UsernameExistsException") {
        return {
          success: false,
          message: "O e-mail já está sendo utilizado.",
        }
      }
      return {
        success: false,
        message: "Erro ao fazer o cadastro, contate o Administrador.",
      };
    }
  };

  const signOut = async () => {
    try {
      await Auth.signOut();
      setUsername("");
      setMail("");
      setName("");
      setIsAuthenticated(false);
      setIdToken("")
      setAccessToken("")
      setRefreshToken("")

      setAuthState({
        isAuthenticated: false,
        username: '',
        mail: '',
        name: '',
        idToken: '',
        accessToken: '',
        refreshToken: ''
      })
      clearSalesWarmupStamp();
      posthog?.reset();
      return { success: true, message: "" };
    } catch (error) {
      return {
        success: false,
        message: `${error}`,
      };
    }
  };

  const sendConfirmationEmail = async (email: string) => {
    Amplify.configure({
      Auth: {
        region: generalConfig.REGION,
        userPoolId: generalConfig.USER_POOL_ID,
        userPoolWebClientId: generalConfig.USER_POOL_APP_CLIENT_ID,
      },
    });
    try {
      const response = await Auth.resendSignUp(email);
      console.log(response)
      return { success: true, message: "" };
    } catch (error) {
      return {
        success: false,
        message: "Erro ao enviar e-mail de confirmação.",
      };
    }
  };



  const confirmEmail = async (email: string, code: string) => {
    Amplify.configure({
      Auth: {
        region: generalConfig.REGION,
        userPoolId: generalConfig.USER_POOL_ID,
        userPoolWebClientId: generalConfig.USER_POOL_APP_CLIENT_ID,
      },
    });
    try {
      const response = await Auth.confirmSignUp(email, code);
      console.log(response)
      return { success: true, message: "" };
    } catch (error) {
      return {
        success: false,
        message: "Erro ao confirmar e-mail, contate o Administrador.",
      };
    }
  };

  const recoverPassword = async (email: string) => {
    Amplify.configure({
      Auth: {
        region: generalConfig.REGION,
        userPoolId: generalConfig.USER_POOL_ID,
        userPoolWebClientId: generalConfig.USER_POOL_APP_CLIENT_ID,
      },
    });
    try {
      console.log(email)
      await Auth.forgotPassword(email);
      return { success: true, message: "" };
    } catch (error) {
      return {
        success: false,
        message: "Erro ao recuperar senha, contate o Administrador.",
      };
    }
  };

  const sendRecoverPasswordCode = async (email: string, code: string, newPassword: string) => {
    Amplify.configure({
      Auth: {
        region: generalConfig.REGION,
        userPoolId: generalConfig.USER_POOL_ID,
        userPoolWebClientId: generalConfig.USER_POOL_APP_CLIENT_ID,
      },
    });
    try {
      await Auth.forgotPasswordSubmit(email, code, newPassword);
      return { success: true, message: "" };
    } catch (error) {
      return {
        success: false,
        message: "Erro ao recuperar senha, contate o Administrador.",
      };
    }
  }; 

  


  return {
    isAuthenticated,
    isAdmin,
    username,
    mail,
    name,
    idToken,
    accessToken,
    refreshToken,
    customAttributes,
    loading,
    salesPingLoading,
    ensureSalesWarmup,
    signIn,
    signUp,
    signOut,
    sendConfirmationEmail,
    confirmEmail,
    recoverPassword,
    sendRecoverPasswordCode,
    creditsKey,
    setcreditsKey
  };
};